import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';

import { JwtModule } from '@auth0/angular-jwt';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ThemeService } from '@services/theme/theme.service';
import { TranslationsService } from '@services/translate/translations.service';
import { PrimeNgLocaleService } from '@services/translate/primeng-locale.service';
import { ProfileUserService } from '@services/profile-user/profile-user.service';
import { NumericFormatService } from '@services/numericFormat/numeric-format.service';
import { PlatformCurrencyService } from '@services/platformCurrency/platform-currency.service';
import { RoleCatalogService } from '@services/roles/role-catalog.service';
import { routes } from './app.routes';

/**
 * Configuración raíz de la aplicación Angular.
 * Registra los proveedores globales: router, HTTP con interceptor de auth, animaciones,
 * PrimeNG con tema Aura, JWT, traducciones y los inicializadores de tema, traducciones, perfil de usuario
 * y catálogo de roles.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    MessageService,
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
    provideAppInitializer(() => inject(ThemeService).load()),
    provideAppInitializer(() => inject(TranslationsService).load()),
    // Textos internos de PrimeNG (paginador, tablas, mensajes vacíos) desde la sección `primeng`.
    provideAppInitializer(() => {
      inject(PrimeNgLocaleService);
    }),
    provideAppInitializer(() => inject(ProfileUserService).load()),
    provideAppInitializer(() => inject(NumericFormatService).load()),
    provideAppInitializer(() => inject(PlatformCurrencyService).load()),
    // Ids de rol reales del ambiente (resueltos por slug); los guards y componentes comparan contra ellos.
    provideAppInitializer(() => inject(RoleCatalogService).load()),
    importProvidersFrom([
      JwtModule.forRoot({
        config: {
          allowedDomains: ['localhost:4200', 'localhost:5000'],
        },
      }),
    ]),
    ...provideTranslateHttpLoader(),
    provideTranslateService({
      fallbackLang: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      },
    }),
  ],
};
